// RFC 6962 Merkle tree over site heads. See docs/spec/events.md §5.
import { concat, fromHex, sha256Hex, toHex } from "./bytes.ts";
import { canonicalBytes } from "./canonical.ts";

export interface SiteHead { site_id: string; seq: number; hash: string }

const LEAF = new Uint8Array([0x00]);
const NODE = new Uint8Array([0x01]);

export const leafHash = (h: SiteHead) => sha256Hex(concat(LEAF, canonicalBytes({ site_id: h.site_id, seq: h.seq, hash: h.hash })));
const nodeHash = (l: string, r: string) => sha256Hex(concat(NODE, fromHex(l), fromHex(r)));

const EMPTY_ROOT = "e3b0c44298fc1c149afbf4c8996fb92427ae41e4649b934ca495991b7852b855"; // SHA-256("")

// Largest power of two strictly less than n (RFC 6962 §2.1 split point).
const split = (n: number) => { let k = 1; while (k * 2 < n) k *= 2; return k; };

async function mth(leaves: string[]): Promise<string> {
  if (leaves.length === 0) return EMPTY_ROOT;
  if (leaves.length === 1) return leaves[0]!;
  const k = split(leaves.length);
  return nodeHash(await mth(leaves.slice(0, k)), await mth(leaves.slice(k)));
}

async function path(m: number, leaves: string[]): Promise<string[]> {
  if (leaves.length <= 1) return [];
  const k = split(leaves.length);
  return m < k
    ? [...(await path(m, leaves.slice(0, k))), await mth(leaves.slice(k))]
    : [...(await path(m - k, leaves.slice(k))), await mth(leaves.slice(0, k))];
}

export const sortHeads = (heads: SiteHead[]) => [...heads].sort((a, b) => (a.site_id < b.site_id ? -1 : a.site_id > b.site_id ? 1 : 0));

export interface MerkleBuild { root: string; leaves: string[]; proofs: Record<string, { index: number; size: number; path: string[] }> }

/** Builds the tree over heads sorted by site_id; proofs are keyed by site_id. */
export async function buildTree(heads: SiteHead[]): Promise<MerkleBuild> {
  const sorted = sortHeads(heads);
  const leaves = await Promise.all(sorted.map(leafHash));
  const proofs: MerkleBuild["proofs"] = {};
  for (let i = 0; i < sorted.length; i++) proofs[sorted[i]!.site_id] = { index: i, size: sorted.length, path: await path(i, leaves) };
  return { root: await mth(leaves), leaves, proofs };
}

/** RFC 9162 §2.1.3.2 inclusion verification. */
export async function verifyInclusion(leaf: string, index: number, size: number, proof: string[], root: string): Promise<boolean> {
  if (index >= size) return false;
  let fn = index, sn = size - 1, r = leaf;
  for (const p of proof) {
    if (sn === 0) return false;
    if (fn % 2 === 1 || fn === sn) {
      r = await nodeHash(p, r);
      if (fn % 2 === 0) while (fn % 2 === 0 && fn !== 0) { fn >>= 1; sn >>= 1; }
    } else {
      r = await nodeHash(r, p);
    }
    fn >>= 1; sn >>= 1;
  }
  return sn === 0 && r === root;
}

export { toHex };
